import { Component, Input, OnChanges, OnInit, SimpleChanges, AfterViewInit } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ColorInfo, EState, ModuleView, _stateColors } from '../../current-agent-states.models';

@Component({
    selector: 'cv-current-agent-states-pm-graph',
    templateUrl: './current-agent-states-pm-graph.component.html',
    styleUrls: ['./current-agent-states-pm-graph.component.scss']
})
export class CurrentAgentStatesPmGraphComponent implements OnInit, OnChanges, AfterViewInit {

    @Input() _activeView: ModuleView;
    @Input() sharedData: any;
    @Input() readOnly: any;
    @Input() _stateToColorMap: ColorInfo;

    _counts = []; // Will contain a list of objects: { stateName, color, count }
    _graphCanvas = null; // Will be the jQuery-wrapped DOM element of the <canvas>
    _chartConstants = {
        fontColor: "#353a40", // theUpsideDown color
        fontFamily: "'Lato-Regular', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
    };

    chartConfig = {
        type: "bar",
        data: {
            labels: [],
            datasets: [
                {
                    label: "Agents", // set to localized value in ctrl.$onInit()
                    backgroundColor: [],
                    data: [],
                }
            ],
        },
        options: {
            indexAxis: "y",
            plugins: {
                emptyChart: {
                    disableCxone: false,
                },
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        labelColor: ctx => ({
                            borderColor: 'rgba(255,255,255)',
                            backgroundColor: ctx.dataset.backgroundColor[ctx.dataIndex],
                        })
                    }
                },
                title: {
                    display: true,
                    text: "Agents", // set to localized value in ctrl.$onInit()
                    font: {
                        family:  this._chartConstants.fontFamily,
                    },
                    color: this._chartConstants.fontColor,
                },
            },
            maintainAspectRatio: false,
            responsive: true,
            scales: {
                x: {
                    beginAtZero: true, // Always show the first marker as zero
                    ticks: {
                        font: {
                            family: this._chartConstants.fontFamily,
                        },
                        color: this._chartConstants.fontColor,
                    }
                },
                y: {
                    ticks: {
                        font: {
                            family: this._chartConstants.fontFamily,
                        },
                        color: this._chartConstants.fontColor,
                    }
                },
            },
        },
    } as ChartConfiguration;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private config: CvConfigurationService,
    ) { }

    ngOnInit(): void {
        this.chartConfig.data.datasets[0].label = this.cvLocalizationService.getCachedText("Agents", "Agents");
        this.chartConfig.options.plugins.title.text = this.cvLocalizationService.getCachedText("Agents", "Agents");
    }

    // once the view is initialized setup the graph
    ngAfterViewInit(): void {
        this.setupGraph();
    }

    // Sets up the ChartJS object.
    setupGraph() {
        this.updateCounts();
        const data = this.updateGraph();
        this.chartConfig.data = data;
    }

    /**
     * Returns the color for the specified state value.  The user may have specified a custom color for that state, which is returned instead of the default colors.
     *
     * @param state AgentState object to categorize. TYPE AgentState
     */
    getStateColor(state) {
        if (state && state.State !== undefined) {
            const colorMapKey = state.DisplayState.toUpperCase();
            if (this._stateToColorMap && this._stateToColorMap[colorMapKey])
                return this._stateToColorMap[colorMapKey];

            // Default Colors:
            if (state.IsACW) {
                return _stateColors.orange;
            }
            if (state.IsDialer) {
                return _stateColors.blue;
            }
            switch (state.State) {
                case EState.Available:
                    return _stateColors.green;
                case EState.Inbound:
                    return _stateColors.purple;
                case EState.Outbound:
                    return _stateColors.yellow;
                case EState.LoggedOut:
                    return _stateColors.gray;
                case EState.Unavailable:
                case EState.Custom:
                default: // Maybe: Mixed
                    return _stateColors.red;
            }
        }
        return _stateColors.red;
    }

    // Runs when the parent's event on data change happens.  Updates the counts for the chart.
    updateCounts() {
        if (this.sharedData.RowValues.length > 0) { // this shouldn't be false, since a check of noData in CurrentAgentStates.aspx (ng-if)
            const states = this.sharedData.RowValues.map(row => row.State);

            const unknownState = this.cvLocalizationService.getCachedText("Current_Agent_States_Module", "Unknown", "Unknown");

            // groupedStates: object where the key is the stateName, the value is an object of: {color: "hex color value", count: 0}
            const groupedStates = states.reduce((groups, state) => {
                const stateName = state.DisplayState || unknownState; // Empty strings or other falsy things get grouped into Unknown
                // if the state hasn't been seen before, set it to 0 before incrementing by one
                groups[stateName] = groups[stateName] ||
                    { color: stateName !== unknownState ? this.getStateColor(state) : "#D3D6DA", count: 0 };
                groups[stateName].count++;
                return groups;
            }, {});

            this._counts = [];
            Object.keys(groupedStates).forEach(key => {
                this._counts.push({
                    stateName: key,
                    color: groupedStates[key].color,
                    count: groupedStates[key].count,
                });
            });
            this._counts.sort((a, b) => a.stateName.localeCompare(b.stateName, undefined, { numeric: true, sensitivity: "accent" }));
        }
    }

    // This function updates the Chart api object with the values of _counts.
    updateGraph() {
        const data = {
            labels: [],
            datasets: [{
                label: this.cvLocalizationService.getCachedText("Agents", "Agents"),
                backgroundColor: [],
                data: [],
            }],
        };

        this._counts.forEach(count => {
            data.labels.push(count.stateName);
            data.datasets[0].backgroundColor.push(count.color);
            data.datasets[0].data.push(count.count);
        });

        this.chartConfig.data = data;

        return data;
    }

    // handler to update the graph when sharedData is changed
    ngOnChanges(changes: SimpleChanges): void {
        if(changes["sharedData"]) {
            this.updateCounts();
            this.updateGraph();
        }
        if(changes["_stateToColorMap"]) {
            const colorMapChanges = changes["_stateToColorMap"];
            if (JSON.stringify(colorMapChanges.currentValue) === JSON.stringify(colorMapChanges.previousValue))
                return;
            this.updateCounts();
            this.updateGraph();
        }
    }
}
